import {
  Alert,
  Button,
  Link,
  Code as OrbitCode,
  Spinner,
  StatusBadge,
} from '@apollo/orbit';
import React from 'react';

import { Code } from 'src/components/code/Code';
import Config from 'src/lib/config';
import { notProduction } from 'src/lib/notProduction';
import { DocsRouter } from 'src/lib/routers';

// This func could eventually make its way into the <Code/> component
export const collapseToShellCommand = (...arr: (string | false)[]): string =>
  arr
    .filter((a: string | boolean) => a)
    .map((str, i: number) => (i > 0 ? `  ${str}` : str))
    .join(' \\\n');

const RoverIntroInstructions = ({
  firstPublish,
}: {
  firstPublish: boolean;
}) => (
  <>
    <p>
      Rover is our command-line tool for interacting with the schema registry.{' '}
      <Link
        target="_blank"
        rel="noopener noreferrer"
        to={DocsRouter.path('RoverGettingStarted')}
      >
        Install Rover
      </Link>{' '}
      and{' '}
      {firstPublish ? (
        <>follow the instructions below to start your first publish.</>
      ) : (
        <>
          <Link
            target="_blank"
            rel="noopener noreferrer"
            to={DocsRouter.path('RoverConfigure')}
          >
            set up your CI/CD pipeline
          </Link>{' '}
          with one of the options below to automatically keep your schema
          current in Studio on each code commit.
        </>
      )}
    </p>
  </>
);

export const ContinuousIntegrationTab = ({
  firstPublish,
  graphId,
  graphVariant,
  serviceApiToken,
  onClose,
}: {
  firstPublish: boolean;
  graphId: string;
  graphVariant: string;
  serviceApiToken: string;
  onClose?: () => void;
}) => {
  let highlightOffset = 0;

  if (notProduction) highlightOffset += 1;
  if (firstPublish) highlightOffset += 1;

  const fileSchemaScript = collapseToShellCommand(
    firstPublish && `APOLLO_KEY=${serviceApiToken}`,
    notProduction && `APOLLO_REGISTRY_URL=${Config.stagingSchemaEndpoint}`,
    `rover graph publish ${graphId}@${graphVariant}`,
    `--schema ./schema.graphql`,
  );

  const introspectSchemaScript = collapseToShellCommand(
    'rover graph introspect',
    'http://localhost:4000 |',
    firstPublish && `APOLLO_KEY=${serviceApiToken}`,
    notProduction && `APOLLO_REGISTRY_URL=${Config.stagingSchemaEndpoint}`,
    `rover graph publish ${graphId}@${graphVariant} --schema -`,
  );

  return (
    <>
      <RoverIntroInstructions firstPublish={firstPublish} />
      <p className="pt-4">
        <b>Option 1:</b> Use a local file containing the schema (e.g., called{' '}
        <OrbitCode>schema.graphql</OrbitCode>)
      </p>
      <Code
        title="schema.graphql"
        className="mt-2"
        showCopyButton={true}
        firstLineDecorator="$ "
        highlight={[1 + highlightOffset]}
        mask={serviceApiToken.split(':').pop()}
        code={fileSchemaScript}
      />
      <p className="pt-4">
        <b>Option 2:</b> Introspect a running server (e.g., at{' '}
        <OrbitCode>http://localhost:4000</OrbitCode>)
      </p>
      <Code
        className="mt-2"
        showCopyButton={true}
        firstLineDecorator="$ "
        highlight={[2]}
        mask={serviceApiToken.split(':').pop()}
        code={introspectSchemaScript}
      />
      <Alert status="info" className="mt-4">
        Visit the docs on{' '}
        <Link
          target="_blank"
          rel="noopener noreferrer"
          to={DocsRouter.path('RoverCiCdSetup')}
        >
          configuring Rover in CI/CD
        </Link>{' '}
        for more suggestions and detailed setup instructions. If you're still
        using the <OrbitCode>apollo</OrbitCode> CLI, see the instructions for{' '}
        <a
          className="text-nowrap"
          href="https://www.npmjs.com/package/apollo/v/2.33.1#apollo-servicepush"
          target="_blank"
          rel="noopener noreferrer"
        >
          <OrbitCode>apollo service:push</OrbitCode>
        </a>{' '}
        command.
      </Alert>
      <div className="mt-8 flex items-center">
        {firstPublish && (
          <StatusBadge icon={<Spinner size="xs" />}>
            Awaiting schema
          </StatusBadge>
        )}
        {onClose && (
          <div className="flex flex-1 justify-end">
            <Button
              variant="primary"
              type="submit"
              className="flex-none"
              onClick={onClose}
            >
              Done
            </Button>
          </div>
        )}
      </div>
    </>
  );
};
